/* eslint-disable react/no-array-index-key */
import {Typography, css, useTheme} from '@mui/material';
import {ConfigObjectType, GetStylesFunction, useApiQuery, DlMuiChip} from '@wallaby-ui/core';
import * as React from 'react';
import {useMemo} from 'react';

import {getRecommendationTooltip} from './PhRecommendationFollowUpDateFormatter';
import {MerieSchema} from '../../types';
import {Formatters} from '../light';
import {MedicationAnnotation, RISK_FACTOR_LABEL_COLORS, TimelineAnnotation} from 'src/js/containers/hospitalizations';

export type PhRecommendation = MerieSchema.components['schemas']['PharmaRecommendationDto'];
export type RiskFactorTypeEnum = MerieSchema.components['schemas']['RiskFactorTypeEnum'];
export type RiskFactorLabelEnum = MerieSchema.components['schemas']['RiskFactorLabelEnum'];
export function useRiskFactorToRiskFactorLabel(config: ConfigObjectType) {
  const riskFactorLabelMappingUrl = config.service.url.riskFactorLabelMapping;
  const {data: riskFactorLabelMapping, isLoading} = useApiQuery<
    MerieSchema.components['schemas']['PairRiskFactorTypeEnumRiskFactorLabelEnum'][]
  >([riskFactorLabelMappingUrl], null, {
    axiosConfig: {
      url: riskFactorLabelMappingUrl,
    },
  });
  const riskFactorToRiskFactorLabel = useMemo(() => {
    const acc = {} as Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
    riskFactorLabelMapping?.forEach((v) => {
      acc[v.first] = v.second;
    });
    return acc;
  }, [riskFactorLabelMapping]);
  return {riskFactorToRiskFactorLabel, riskFactorToRiskFactorLabelLoading: isLoading};
}
export function getIsPhRecUnpublished(phRec: Pick<PhRecommendation, 'isDirty'> | undefined): boolean {
  return phRec?.isDirty ?? true;
}

type RiskFactorLabelProps = {
  annotation: MerieSchema.components['schemas']['TimelineEventAnnotationDto'];
  formatters: Formatters;
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
};

export const AnnotationTooltipRiskFactorLabel: React.FC<RiskFactorLabelProps> = ({annotation, formatters, riskFactorToRiskFactorLabel}) => {
  const {t, tEnum} = formatters;

  const riskFactors = annotation.riskFactorTypes.map((v) => {
    const rawRiskFactorType = v;
    const rawRiskFactorLabel = riskFactorToRiskFactorLabel[rawRiskFactorType];
    const riskFactorLabel = tEnum('RiskFactorLabelEnum', rawRiskFactorLabel);
    const riskFactorType = tEnum('RiskFactorTypeEnum', rawRiskFactorType);
    return {
      rawRiskFactorLabel,
      riskFactorLabel,
      riskFactorType,
    };
  });
  const theme = useTheme();
  const styles = getStyles({theme, params: {}});
  return (
    <>
      {riskFactors.length > 0 && (
        <>
          <div css={styles.divider} />
          <div css={styles.sectionWrapper}>
            <Typography variant='labelBold'>{t('timeline.annotationTooltip.riskFactor')}</Typography>
            {riskFactors.map((v, i) => (
              <div key={i} css={styles.riskFactorWrapper}>
                <DlMuiChip
                  label={v.riskFactorLabel}
                  style={{
                    backgroundColor: v.rawRiskFactorLabel ? RISK_FACTOR_LABEL_COLORS[v.rawRiskFactorLabel].color : 'transparent',
                  }}
                />
                <Typography variant='value'>{v.riskFactorType}</Typography>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

type AnnotationType = MerieSchema.components['schemas']['EncounterTimelineEventDto']['annotations'][0];
export function getNonMedicationAnnotationText(annotation: AnnotationType, t: (input: string) => string) {
  return annotation.pharmaRecommendationData ? t('hospitalization.detail.timeline.annotation.phRec') : annotation.annotation;
}

type PharmaRecommendation = Omit<MerieSchema.components['schemas']['PharmaRecommendationEventDataDto'], 'annotation'>;

type Props = {
  encounterId: number | undefined;
  data: MerieSchema.components['schemas']['EncounterTimelineEventDto'];
  config: ConfigObjectType;
  formatters: Formatters;
  versionSuffix: string;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  onPhRecAnnotationClicked?: (pharmaRecommendation: PharmaRecommendation) => void;
};
export const HospitalizationDetailTimelineAnnotationFormatter: React.FC<Props> = (props) => {
  const {encounterId, data, config, formatters, versionSuffix, pharmaRecommendations, onPhRecAnnotationClicked} = props;
  const value = data.annotations;
  const theme = useTheme();
  const styles = getStyles({theme, params: {}});
  const {t} = formatters;

  const isMedication = data?.eventType === 'MEDICATION';
  const isLabResult = (data?.eventType ?? '').startsWith('LAB_');
  // const isMicrobiology = (data?.eventType ?? '').startsWith('CULTIVATION_');

  const {riskFactorToRiskFactorLabel} = useRiskFactorToRiskFactorLabel(config);

  return isMedication ? (
    <MedicationAnnotation
      versionSuffix={versionSuffix}
      encounterId={encounterId}
      data={value}
      riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
      formatters={formatters}
    />
  ) : (
    <>
      {value.map((annotation, i) => {
        const annotationText = getNonMedicationAnnotationText(annotation, t);
        const annotationTitle = annotation.annotationTooltip ?? annotationText;
        let labGroup = '';
        if (isLabResult) labGroup = t('timeline.annotationTooltip.labGroup.labResult');
        const pharmaRecommendation =
          annotation?.pharmaRecommendationData?.pharmaRecommendationId != null
            ? (pharmaRecommendations?.pharmaRecommendations || []).find(
                (rec) => rec.id === annotation.pharmaRecommendationData?.pharmaRecommendationId
              )
            : undefined;
        const annotationTooltip = pharmaRecommendation ? (
          getRecommendationTooltip(
            getIsPhRecUnpublished(pharmaRecommendation),
            pharmaRecommendation.checked ? undefined : pharmaRecommendation.followUpDate,
            pharmaRecommendation.lastPublishDate,
            pharmaRecommendation.conclusion,
            pharmaRecommendation.riskiness,
            formatters
          )
        ) : (
          <div css={styles.annotationWrapper}>
            <Typography variant='value' css={styles.title}>
              {annotationTitle}
            </Typography>
            {labGroup && <div css={styles.divider} />}
            {labGroup && (
              <div css={styles.sectionWrapper}>
                <Typography variant='value'>{labGroup}</Typography>
              </div>
            )}
            <AnnotationTooltipRiskFactorLabel
              annotation={annotation}
              formatters={formatters}
              riskFactorToRiskFactorLabel={riskFactorToRiskFactorLabel}
            />
          </div>
        );
        return (
          <TimelineAnnotation
            key={`${annotation.documentVersionId || annotation.pharmaRecommendationData?.pharmaRecommendationId}-${i}`}
            versionSuffix={versionSuffix}
            encounterId={encounterId}
            documentVersionId={annotation.documentVersionId}
            pharmaRecommendation={annotation.pharmaRecommendationData}
            highlightType={annotation.highlightType}
            tooltip={annotationTooltip}
            isDiscontinued={annotation.medicationData?.isDiscontinued}
            onPhRecAnnotationClicked={onPhRecAnnotationClicked}
          >
            {annotationText}
          </TimelineAnnotation>
        );
      })}
    </>
  );
};
HospitalizationDetailTimelineAnnotationFormatter.displayName = 'HospitalizationDetailTimelineAnnotationFormatter';

const getStyles: GetStylesFunction = (options) => ({
  annotationWrapper: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  title: css`
    white-space: pre-wrap;
  `,
  divider: css`
    border-top: 1px solid ${options?.theme?.palette.custom.coolGray['500']};
    width: 100%;
    height: 0;
  `,
  sectionWrapper: css`
    display: flex;
    flex-direction: column;
  `,
  riskFactorWrapper: css`
    margin-top: 4px;
    display: flex;
    gap: 4px;
    align-items: center;
  `,
});
